import axios from 'axios';
import { Product, ApiResponse, LoginRequest, LoginResponse, ProductFormData } from '../types';

// Get the current hostname and use it for API calls
// This allows the app to work on both localhost and network access
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  const hostname = window.location.hostname;
  const port = '4010';
  
  // If accessing via localhost, use localhost for API
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `http://localhost:${port}`;
  }
  
  // If accessing via network IP, use the same IP for API
  return `http://${hostname}:${port}`;
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const productService = {
  // Get all products
  getAllProducts: async (): Promise<Product[]> => {
    const response = await api.get<ApiResponse<Product[]>>('/api/products/allproducts');
    return response.data.data || [];
  },

  // Delete product
  deleteProduct: async (id: number): Promise<void> => {
    await api.delete('/api/products/deleteproduct', { data: { id } });
  },

  // Update product
  updateProduct: async (id: number, productData: ProductFormData): Promise<void> => {
    const requestData = {
      id,
      name: productData.name,
      category: productData.category,
      price: productData.price,
      length: productData.length,
      height: productData.height,
      depth: productData.depth,
      material: productData.material,
      img_path: productData.img_path || '',
      glb_path: productData.glb_path || '',
      usd_path: productData.usd_path || '',
    };

    await api.put('/api/products/updateproduct', requestData);
  },

  // Add product
  addProduct: async (productData: ProductFormData): Promise<void> => {
    const requestData = {
      name: productData.name,
      category: productData.category,
      price: productData.price,
      length: productData.length,
      height: productData.height,
      depth: productData.depth,
      material: productData.material,
      img_path: productData.img_path || '',
      glb_path: productData.glb_path || '',
      usd_path: productData.usd_path || '',
    };

    await api.post('/api/products/addproduct', requestData);
  },
};

export const authService = {
  // Login
  login: async (adminKey: string): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/api/auth/login', { adminKey });
    return response.data;
  },
};

export default api;
